import React from 'react';
import { ExpandButton } from 'src/domains/sportsbook/webview/components/expandButton/ExpandButton';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import {
    GoalScorerMarket,
    GoalScorerMarketListItemWrapper,
} from 'src/domains/sportsbook/webview/components/goalscorerMarketGroup/GoalScorerMarket';
import { observer } from 'src/utils/mobx-react';
import { FiltersNewView } from 'src/domains/layouts/webview/components/filters/Filters';
import { EventId, MarketId } from 'src_common/common/websocket2/id/WebsocketId';
import { GoalscorerMarketGroupState } from './GoalscorerMarketGroup.state';
import {
    Caption,
    CaptionGroup,
    MarketGroupCaption,
    Goalscorers,
    MarketGroupFilters,
    SelectionsGroup,
    SelectionsGroupWrap,
} from 'src/domains/sportsbook/webview/components/goalscorerMarketGroup/GoalscorerMarketGroup.style';
import {
    GoalScorerMarketState,
    GoalScorerType,
} from 'src/domains/sportsbook/webview/components/goalscorerMarketGroup/GoalScorerMarket.state';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { BreakpointsState } from 'src/domains/layouts/state/breakpointsState/BreakpointsState';
import { MarketHeader } from 'src/domains/sportsbook/webview/components/marketHeader/MarketHeader';
import { MARKET_NAMES_UNIFIED } from 'src/domains/sportsbook/webview/components/goalscorerMarketGroupUnified/GoalScorerMarket';

interface PropsType {
    eventId: EventId;
    markets: Array<MarketId>;
    selections: Record<string, Array<SelectionModel>>;
    isOpen: boolean;
    onClick: () => void;
}
export const MARKET_NAMES: Record<string, JSX.Element> = {
    'first-goalscorer': (
        <I18n
            langKey='events.goalscorer-market.first'
            defaultText='First'
        />
    ),
    'anytime-goalscorer': (
        <I18n
            langKey='events.goalscorer-market.anytime'
            defaultText='Anytime'
        />
    ),
    'hattrick-goalscorer': (
        <I18n
            langKey='events.goalscorer-market.hattrick'
            defaultText='Hat-trick'
        />
    ),
};

function getTemplateIds(markets: Array<MarketId>): Array<string | undefined> {
    return markets.map((id) => id.getModel()?.newTemplateId);
}

export const GoalscorerMarketGroup = observer('GoalscorerMarketGroup', (props: PropsType) => {
    const { isOpen, onClick, eventId, markets } = props;

    const marketId = markets[0] ?? null;

    if (marketId === null) {
        return null;
    }

    return (
        <div className='market-group--goalscorer'>
            <MarketHeader
                name='First Goalscorer'
                isOpen={isOpen}
                onClick={onClick}
                marketId={marketId}
            />
            {isOpen ? (
                <GoalscorerMarketGroupBody
                    eventId={eventId}
                    markets={props.markets}
                    selections={props.selections}
                />
            ) : null}
        </div>
    );
});

export const GoalscorerMarketGroupBody = observer(
    'GoalscorerMarketGroup',
    (props: { selections: Record<string, Array<SelectionModel>>; eventId: EventId; markets: Array<MarketId> }) => {
        const { selections, eventId, markets } = props;
        const common = useCommon();
        const [state] = React.useState(() => new GoalscorerMarketGroupState());
        const goalScorerState = new GoalScorerMarketState(selections);

        const configState = ConfigComponents.get(common).unifiedComponentConfig;
        const breakpointsState = BreakpointsState.get(common);

        const { sportsbookVariant } = configState.componentVariants;

        const event = eventId.getEventModel();
        if (event === null) {
            return null;
        }

        const { open, isExpanded, toggleExpand, handleOpenHome, handleOpenAway } = state;
        const getGoalscorers = (openOption: 'H' | 'A'): GoalScorerType[] => {
            const noGoalScore = goalScorerState.noGoalScore === null ? [] : [goalScorerState.noGoalScore];

            switch (openOption) {
                case 'H':
                    return goalScorerState.goalScorerListHome.concat(noGoalScore);
                case 'A':
                    return goalScorerState.goalScorerListAway.concat(noGoalScore);
            }
        };

        const participants = event.participants;
        const marketId = markets[0] ?? null;

        if (marketId === null) {
            return null;
        }

        const homeName: string | null = Object.values(participants).find((x) => x.role === 'home')?.name ?? null;
        const awayName: string | null = Object.values(participants).find((x) => x.role === 'away')?.name ?? null;

        const goalscorers = getGoalscorers(open);
        // Show more/less button only if there
        // are more then 6 participants
        const expandButton =
            goalscorers.length > 6 ? (
                <ExpandButton
                    isExpanded={isExpanded}
                    toggleExpand={toggleExpand}
                />
            ) : null;

        const isLargeDesktop = breakpointsState.largeDesktop.isBiggerOrEq;

        const filters = [
            {
                key: 'H',
                label:
                    homeName === null ? (
                        <I18n
                            langKey='events.selections-group.team.home'
                            defaultText='Home'
                        />
                    ) : (
                        homeName
                    ),
                onClick: handleOpenHome,
            },
            {
                key: 'A',
                label:
                    awayName === null ? (
                        <I18n
                            langKey='events.selections-group.team.away'
                            defaultText='Away'
                        />
                    ) : (
                        awayName
                    ),
                onClick: handleOpenAway,
            },
        ];

        const marketNames = configState.unifiedSportEventsTable ? MARKET_NAMES_UNIFIED : MARKET_NAMES;

        return (
            <SelectionsGroupWrap variant={sportsbookVariant}>
                {isLargeDesktop === true ? (
                    <GoalScorerMarket state={goalScorerState} />
                ) : (
                    <>
                        <MarketGroupFilters
                            className='market-group__teams'
                            variant={sportsbookVariant}
                        >
                            <FiltersNewView
                                select={open}
                                skin={configState.filtersVersion.football}
                                filters={filters}
                            />
                        </MarketGroupFilters>

                        <MarketGroupCaption variant={sportsbookVariant}>
                            <Caption
                                isFirst={true}
                                variant={sportsbookVariant}
                            >
                                <I18n
                                    langKey='events.goalscorer-market.player'
                                    defaultText='Player'
                                />
                            </Caption>
                            <CaptionGroup variant={sportsbookVariant}>
                                {Object.keys(marketNames)
                                    .filter((id) => getTemplateIds(markets).includes(id))
                                    .map((templateId: string) => {
                                        const marketName = marketNames[templateId];
                                        return (
                                            <Caption
                                                key={templateId}
                                                variant={sportsbookVariant}
                                            >
                                                {marketName}
                                            </Caption>
                                        );
                                    })}
                            </CaptionGroup>
                        </MarketGroupCaption>

                        <SelectionsGroup variant={sportsbookVariant}>
                            <Goalscorers
                                className='market-group__goalscorers'
                                variant={sportsbookVariant}
                            >
                                {goalscorers.slice(0, isExpanded ? goalscorers.length : 9).map((value, idx) => {
                                    return (
                                        <GoalScorerMarketListItemWrapper
                                            key={`${idx}-${value.name}`}
                                            goalScorer={value}
                                            variant={sportsbookVariant}
                                        />
                                    );
                                })}
                            </Goalscorers>
                        </SelectionsGroup>
                    </>
                )}
                {isLargeDesktop === true ? null : expandButton}
            </SelectionsGroupWrap>
        );
    }
);
